import { appendQueryString } from '@cognitoforms/utils/url-helper';
import type { FileDataRef } from '@cognitoforms/types/server-types/model/file-data-ref';

export type ViewerType = 'image' | 'pdf' | 'video' | 'audio' | 'document' | 'text' | 'download';

const VIEWER_TYPE_PRESENCE: Record<ViewerType, true> = {
	image: true,
	pdf: true,
	video: true,
	audio: true,
	document: true,
	text: true,
	download: true
};

export const VIEWER_TYPES = Object.keys(VIEWER_TYPE_PRESENCE) as ViewerType[];

export interface OpenFileViewerEventData {
	fileId: string;
	fileName?: string | null;
	fieldId?: string | null;
	transientDocument?: {
		fileName?: string | null;
		downloadUrl: string;
		viewerUrl?: string | null;
		contentType?: string | null;
		extension?: string | null;
	};
}

export interface FileViewerFile extends Pick<FileDataRef, 'Id' | 'Name' | 'Size' | 'ContentType'> {
	fileId: string;
	fileName: string;
	fileSize: number;
	contentType: string;
	fieldId: string;
	fieldLabel: string;
	fieldIndex: number;
	globalIndex: number;
	downloadUrl: string;
	viewerUrl: string;
	thumbnailUrl: string;
	extension: string;
	viewerType: ViewerType;
	requiresGeneration?: boolean;
	generatedFormId?: string;
	generatedFieldName?: string;
	templateNumber?: number;
	signing?: unknown;
}

export interface FileViewerField {
	fieldId: string;
	fieldLabel: string;
	files: FileViewerFile[];
}

export interface FileViewerManifest {
	formTitle: string;
	entryTitle: string;
	fields: FileViewerField[];
	files: FileViewerFile[];
}

export const FILE_VIEWER_PAGER_OVERFLOW_THRESHOLD = 30;

const imageExtensions = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'tif', 'tiff']);
const videoExtensions = new Set(['mp4', 'mov', 'webm']);
const audioExtensions = new Set(['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a']);
const documentExtensions = new Set(['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp', 'key']);
const textExtensions = new Set(['md', 'markdown', 'csv', 'txt', 'log', 'json', 'xml', 'yml', 'yaml']);
const knownFileExtensions = new Set([
	...imageExtensions,
	...videoExtensions,
	...audioExtensions,
	...documentExtensions,
	...textExtensions,
	'pdf'
]);

export function getFileExtension(fileName: string | null | undefined): string {
	if (!fileName)
		return '';

	const dotIndex = fileName.lastIndexOf('.');
	if (dotIndex < 0 || dotIndex === fileName.length - 1)
		return '';

	return fileName.substring(dotIndex + 1).toLowerCase();
}

export function isKnownFileTypeExtension(extension: string | null | undefined): boolean {
	return !!extension && knownFileExtensions.has(extension.toLowerCase());
}

export function getDisplayFileName(fileName: string | null | undefined): string {
	if (!fileName)
		return '';

	const extension = getFileExtension(fileName);
	if (!isKnownFileTypeExtension(extension))
		return fileName;

	const extensionLength = extension.length + 1;
	return fileName.length > extensionLength ? fileName.slice(0, -extensionLength) : fileName;
}

export function getViewerType(file: Pick<FileDataRef, 'Name' | 'ContentType'>): ViewerType {
	const extension = getFileExtension(file.Name);
	const contentType = file.ContentType?.toLowerCase() ?? '';

	if (extension === 'pdf' || contentType === 'application/pdf')
		return 'pdf';

	if (imageExtensions.has(extension) || contentType.startsWith('image/'))
		return 'image';

	if (videoExtensions.has(extension) || contentType.startsWith('video/'))
		return 'video';

	if (audioExtensions.has(extension) || contentType.startsWith('audio/'))
		return 'audio';

	if (documentExtensions.has(extension))
		return 'document';

	if (textExtensions.has(extension)
		|| contentType.startsWith('text/')
		|| contentType === 'application/json'
		|| contentType === 'application/xml')
		return 'text';

	return 'download';
}

export function getOfficeOnlineViewerUrl(downloadUrl: string): string {
	return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(downloadUrl)}`;
}

const INLINE_FLAG_PATTERN = /[?&]at=0(?:[&#]|$)/;

/**
 * Appends the server's inline-disposition flag (`at=0`) to a file download URL, unless the
 * URL already carries it. The URL may be relative, so the match is a query-parameter
 * boundary rather than a parse: an unrelated parameter such as `format=0` contains the
 * literal `at=0` and must not be mistaken for the flag.
 */
export function getInlineViewerUrl(downloadUrl: string): string {
	if (!downloadUrl || INLINE_FLAG_PATTERN.test(downloadUrl))
		return downloadUrl;

	return appendQueryString(downloadUrl, { at: '0' });
}

export const PREVIEW_SIZE_CAP_BYTES = 15 * 1024 * 1024;

export function exceedsPreviewSizeCap(fileSizeBytes: number, capBytes: number = PREVIEW_SIZE_CAP_BYTES): boolean {
	return fileSizeBytes > capBytes;
}
